import {
  Component,
  Input,
  Output,
  SimpleChanges,
  EventEmitter,
  OnChanges,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { UntilDestroy } from "@ngneat/until-destroy";
import { FreightShipmentDto } from "../../../core/models/freight-quote/freightShipmentDto";
import { FreightQuoteService } from "../../../core/services/freight-quote.service";
import { GenericResponseDto } from "../../../core/models/generic-response.dto";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { LoaderService } from "../../../core/services/loader.service";
import { CommonModule } from "@angular/common";
import { DialogModule } from "primeng/dialog";
import { TableModule } from "primeng/table";
import { FreightShipmentSectionDto } from "../../../core/models/freight-quote/freightShipmentSectionDto";
import { FreightShipmentSectionPricingDetailDto } from "../../../core/models/freight-quote/freightShipmentSectionPricingDetailDto";
import { FreightShipmentSectionPricingDto } from "../../../core/models/freight-quote/freightShipmentSectionPricingDto";
import { ButtonModule } from "primeng/button";
import { FreightQuotePdfDto } from "../../../core/models/freight-quote/freightQuotePdfDto";
import { InputTextModule } from "primeng/inputtext";
import { CheckboxChangeEvent, CheckboxModule } from "primeng/checkbox";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";

@Component({
  selector: "jni-freight-charges-detail",
  templateUrl: "./freight-charges-detail.component.html",
  styleUrls: ["./freight-charges-detail.component.scss"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    TableModule,
    NgxExtendedPdfViewerModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    FormsModule,
  ],
})
@UntilDestroy()
export class FreightChargesDetailComponent implements OnChanges {
  freightQuotationForm!: FormGroup;
  constructor(
    private fb: FormBuilder,
    private _loaderService: LoaderService,
    private _freightQuoteService: FreightQuoteService,
    private _toastr: JniToastrService
  ) {
    this.formGroup = this.freightQuotationForm;

    this.freightQuotationForm = this.fb.group({
      id: new FormControl(0),
      isPackageDetail: new FormControl(false),
      freightEstimationSections: this.fb.array([
        this.createfreightEstimationSections(),
      ]),
    });
  }

  selectedOptions: Record<string, number> = {};
  @Input() estimations!: FreightShipmentDto;
  @Input() quoteId!: number;
  @Output() multiPricingSelection = new EventEmitter<FreightShipmentDto>();
  @Output() emitQouteTotalPrice = new EventEmitter<string>();
  selectedQuoteOption = 0;
  isQuoteSelected = false;
  formGroup!: FormGroup;
  pdfViewerPage = false;
  base64Pdf = "";
  fileNameToDownload = "";
  selectedPackageModal!: FreightShipmentSectionPricingDetailDto[] | null;
  showPricingDetailDialog = false;
  // selectedShipmentReference: any[] = [];

  createfreightEstimationSections(): FormGroup {
    return this.fb.group({
      sectionId: new FormControl(0),
      shipmentDescription: new FormControl(""),
      shipmentReferenceId: new FormControl(""),
      freightEstimationSectionPricing: this.fb.array([]),
    });
  }

  createFreightEstimationSectionPricing(): FormGroup {
    return this.fb.group({
      id: new FormControl(0),
      isSelected: new FormControl(false),
      shippingCarrier: new FormControl(""),
      shipmentMethodCode: new FormControl(""),
      shipmentMethodDescription: new FormControl(""),
      pricingCurrencyCode: new FormControl(""),
      totalCharges: new FormControl(0),
      discountedTotalCharges: new FormControl(0),
      daysInTransit: new FormControl(0),
      estimatedArrivalDate: new FormControl(""),
      estimatedDeliveryTime: new FormControl(""),
      packageRefernceId: new FormControl(""),
      servicePricingUniqueId: new FormControl(""),
      freightSectionId: new FormControl(0),
      priceIndex: new FormControl(0),
      freightEstimationSectionPricingDetails: this.fb.array([]),
    });
  }

  createSectionPricingDetail(): FormGroup {
    return this.fb.group({
      id: new FormControl(0),
      pricingDetailDescription: new FormControl(""),
      pricingDetailCode: new FormControl(""),
      pricingDetailAmount: new FormControl(0),
      pricingCurrencyCode: new FormControl(""),
      freightEstimationPricingId: new FormControl(0),
    });
  }

  appendFreightEstimationSections() {
    const shipmentArray = this.freightQuotationForm.get(
      "freightEstimationSections"
    ) as FormArray;
    shipmentArray.push(this.createfreightEstimationSections());
  }

  get freightEstimationSections(): FormArray {
    return this.freightQuotationForm.get(
      "freightEstimationSections"
    ) as FormArray;
  }

  getFreightEstimationSections(index: number): FormGroup {
    return this.freightEstimationSections.at(index) as FormGroup;
  }

  getFreightEstimationSectionPricing(shipment: AbstractControl): FormGroup {
    return (
      (shipment.get("freightEstimationSectionPricing") as FormGroup) ??
      this.fb.array([])
    );
  }

  appendFreightEstimationSectionPricing(shipmentIndex: number) {
    const shipmentArray = this.freightQuotationForm.get(
      "freightEstimationSections"
    ) as FormArray;
    const shipment = shipmentArray.at(shipmentIndex) as FormGroup;
    const packages = shipment.get(
      "freightEstimationSectionPricing"
    ) as FormArray;
    packages.push(this.createFreightEstimationSectionPricing());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["estimations"] &&
      changes["estimations"].currentValue !== undefined &&
      changes["estimations"].currentValue
    ) {
      this.freightQuotationForm.patchValue(this.estimations);
      this.patchArrayData(this.estimations.freightEstimationSections);
      this.setGetQuoteSelected();
    }
  }

  patchArrayData(freightSections: FreightShipmentSectionDto[]) {
    if (!freightSections || !Array.isArray(freightSections)) {
      return;
    }

    const shipmentArray = this.freightQuotationForm.get(
      "freightEstimationSections"
    ) as FormArray;
    shipmentArray.clear();

    freightSections.forEach((shipment: FreightShipmentSectionDto) => {
      const shipmentGroup = this.createfreightEstimationSections();
      shipmentGroup.patchValue({
        sectionId: shipment.id,
        shipmentReferenceId: shipment.shipmentReferenceId,
        shipmentDescription: shipment.shipmentDescription || "",
      });

      const pricingArray = shipmentGroup.get(
        "freightEstimationSectionPricing"
      ) as FormArray;
      if (Array.isArray(shipment.freightEstimationSectionPricing)) {
        shipment.freightEstimationSectionPricing.forEach(
          (
            pricingItem: FreightShipmentSectionPricingDto,
            priceIndex: number
          ) => {
            const pricingGroup = this.createFreightEstimationSectionPricing();
            pricingGroup.patchValue({
              ...pricingItem,
              priceIndex: priceIndex,
              freightSectionId: shipment.id,
            });
            pricingArray.push(pricingGroup);

            const pricingDetailsArray = pricingGroup.get(
              "freightEstimationSectionPricingDetails"
            ) as FormArray;
            if (
              Array.isArray(pricingItem.freightEstimationSectionPricingDetails)
            ) {
              pricingItem.freightEstimationSectionPricingDetails.forEach(
                (pricingDetailItem: FreightShipmentSectionPricingDetailDto) => {
                  const pricingDetailGroup = this.createSectionPricingDetail();
                  pricingDetailGroup.patchValue(pricingDetailItem);
                  pricingDetailsArray.push(pricingDetailGroup);
                }
              );
            }
          }
        );
      }
      shipmentArray.push(shipmentGroup);
    });
  }

  getFormGroup(index: number): FormGroup {
    const sections = this.freightQuotationForm.get(
      "freightEstimationSections"
    ) as FormArray;
    return sections.at(index) as FormGroup;
  }

  onSelectQuoteOption(
    shipment: FormGroup,
    priceIndex: number,
    isSelected: CheckboxChangeEvent,
    all?: string
  ) {
    const pricingArray = shipment?.get(
      "freightEstimationSectionPricing"
    ) as FormArray;
    const checkedVal = (isSelected.originalEvent!.target as HTMLInputElement)
      .checked;
    if (all === "All") {
      pricingArray.controls.forEach(item => {
        item.patchValue({ isSelected: checkedVal });
      });
    } else {
      const selectedItem = pricingArray.at(priceIndex);
      selectedItem.patchValue({ isSelected: checkedVal });
    }

    this.setGetQuoteSelected();
    this.multiPricingSelection.emit(this.freightQuotationForm.getRawValue());
  }

  onPackageDetailSelected() {
    this.freightQuotationForm.patchValue({
      isPackageDetail: this.freightQuotationForm.value.isPackageDetail
        ? false
        : true,
    });
  }

  generateQuotation() {
    this.downloadPdf();
  }

  groupByEstimatedArrivalDate(
    packageItems: FreightShipmentSectionPricingDto[]
  ): {
    estimatedDate: string;
    freightEstimationSectionPricings: FreightShipmentSectionPricingDto[];
  }[] {
    const groupedPricing: Record<string, FreightShipmentSectionPricingDto[]> =
      {};
    packageItems.forEach((pricing: FreightShipmentSectionPricingDto) => {
      const estimatedDate = pricing?.estimatedArrivalDate ?? "";
      if (!groupedPricing[estimatedDate]) {
        groupedPricing[estimatedDate] = [];
      }
      groupedPricing[estimatedDate].push(pricing);
    });

    return Object.keys(groupedPricing).map(date => ({
      estimatedDate: date,
      freightEstimationSectionPricings: groupedPricing[date],
    }));
  }

  downloadPdf() {
    this._loaderService.setLoaderState(true);
    this._freightQuoteService
      .generateQoutePdf(this.freightQuotationForm.getRawValue())
      .subscribe((res: GenericResponseDto<FreightQuotePdfDto>) => {
        if (res?.status.toLowerCase() === "success" && res?.data) {
          this._loaderService.setLoaderState(false);
          this.base64Pdf = res.data?.pdfBase64;
          this.fileNameToDownload = `QuotationPDF-${this.freightQuotationForm.value?.id}`;
          this.pdfViewerPage = true;
        } else {
          this._toastr.error(
            "Error",
            res?.message || `Error generating the PDF`
          );
        }
      });
  }

  setGetQuoteSelected() {
    const sectionsArray = this.freightQuotationForm.get(
      "freightEstimationSections"
    ) as FormArray;
    const hasSelected: { isSelected: boolean }[] = [];
    let sectionLength = 0;
    let totalPrice = 0;
    sectionsArray.controls.forEach(section => {
      const pricingArray = section.get(
        "freightEstimationSectionPricing"
      ) as FormArray;
      if (pricingArray && pricingArray.length > 0) {
        sectionLength++;
        const hasAnySelected = pricingArray.controls.some(
          pricing => pricing.value.isSelected
        );
        if (hasAnySelected) {
          const selectedCount = pricingArray.controls.filter(
            pricing => pricing.value.isSelected
          ).length;
          if (selectedCount === 1) {
            const pricingObj = pricingArray.controls.find(
              x => x.value.isSelected
            );
            if (pricingObj) {
              totalPrice += pricingObj.value.discountedTotalCharges ?? 0;
            }
          }
          hasSelected.push({ isSelected: true });
          return;
        }
      }
    });
    this.isQuoteSelected =
      hasSelected.length === sectionLength && sectionLength > 0;
    const roundedTotalPrice = parseFloat(totalPrice.toFixed(2));
    this.emitQouteTotalPrice.emit(this.priceFormat(roundedTotalPrice));
  }

  closeModalPdfViewerPage() {
    this.pdfViewerPage = false;
  }

  trackById(
    index: number,
    item: {
      estimatedDate: string;
      freightEstimationSectionPricings: FreightShipmentSectionPricingDto[];
    }
  ): number {
    return item?.freightEstimationSectionPricings[index]?.id ?? index;
  }

  hasSomeEstimations(estimation: AbstractControl): boolean {
    const pricing = estimation.get("freightEstimationSectionPricing")
      ?.value as FreightShipmentSectionPricingDto[];
    return this.groupByEstimatedArrivalDate(pricing).length > 0;
  }
  get hasValidEstimations(): boolean {
    return this.freightEstimationSections?.controls.some(
      estimation =>
        this.groupByEstimatedArrivalDate(
          estimation.value
            .freightEstimationSectionPricing as FreightShipmentSectionPricingDto[]
        ).length > 0
    );
  }

  showOrHidePricingModal(packageData: FreightShipmentSectionPricingDto) {
    this.selectedPackageModal = packageData
      ? packageData.freightEstimationSectionPricingDetails
      : null;

    if (!this.selectedPackageModal) {
      this.showPricingDetailDialog = true;
    } else {
      this.showPricingDetailDialog = true;
    }
  }

  priceFormat(value: number | null): string {
    const safeValue = typeof value === "number" && !isNaN(value) ? value : 0;
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeValue);
  }
}
